import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ChevronLeft, Activity, Flame, Sparkles, Snowflake, AlertCircle, Clock,
  StickyNote, PhoneCall, Mail as MailIcon, MessageCircle, File as FileIcon,
  ActivitySquare, User, Zap, TrendingUp, Loader2,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { cn } from '../../utils/tailwindUtils';
import {
  getEngageStatusBadge, ENGAGE_STATUS_LABEL,
} from '../../utils/engageStageBadge';


const GRADE_META = {
  Hot:  { tone: 'from-red-500 to-orange-500',    text: 'text-red-600 dark:text-red-400',    bg: 'bg-red-50 dark:bg-red-900/20',    icon: Flame,     label: 'Hot' },
  Warm: { tone: 'from-amber-500 to-yellow-500',  text: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-900/20', icon: Sparkles,  label: 'Warm' },
  Cold: { tone: 'from-slate-400 to-slate-500',   text: 'text-slate-600 dark:text-slate-400', bg: 'bg-slate-50 dark:bg-slate-900/20', icon: Snowflake, label: 'Cold' },
};

const SEVERITY_META = {
  high:   { tone: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300', dot: 'bg-red-500' },
  medium: { tone: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300', dot: 'bg-amber-500' },
  low:    { tone: 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300', dot: 'bg-blue-500' },
};

const LOG_TYPE_META = {
  note:     { icon: StickyNote,   tone: 'text-amber-500',  label: 'Notes' },
  call:     { icon: PhoneCall,    tone: 'text-green-500',  label: 'Calls' },
  whatsapp: { icon: MessageCircle, tone: 'text-emerald-500', label: 'WhatsApp' },
  email:    { icon: MailIcon,     tone: 'text-blue-500',   label: 'Emails' },
  sms:      { icon: MessageCircle, tone: 'text-cyan-500',   label: 'SMS' },
  document: { icon: FileIcon,     tone: 'text-purple-500', label: 'Documents' },
  status:   { icon: ActivitySquare, tone: 'text-indigo-500', label: 'Status changes' },
  system:   { icon: Activity,     tone: 'text-slate-500',  label: 'System events' },
};


const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-IN', {
  day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
}) : '—';

const daysAgo = (iso) => {
  if (!iso) return null;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
};


export default function PatientEngagementDashboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetch: apiFetch } = useApiMethods();

  const [enquiry, setEnquiry] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [eResp, engResp, lResp] = await Promise.all([
          apiFetch(`engage/enquiries/${id}/`),
          apiFetch(`engage/analytics/patient-engagement/${id}/`),
          apiFetch(`engage/activity-logs/?enquiry=${id}`),
        ]);
        if (cancelled) return;
        setEnquiry(eResp?.data || eResp?.data?.data || null);
        setEngagement(engResp?.data || engResp?.data?.data || null);
        const rawLogs = lResp?.results || lResp?.data?.results || [];
        setLogs(Array.isArray(rawLogs) ? rawLogs : []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load engagement data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Activity breakdown chart (sorted by count desc)
  const breakdownRows = useMemo(() => {
    if (!engagement?.breakdown) return [];
    const total = Object.values(engagement.breakdown).reduce((s, n) => s + n, 0) || 1;
    return Object.entries(engagement.breakdown)
      .map(([type, count]) => ({ type, count, pct: Math.round(count / total * 100) }))
      .sort((a, b) => b.count - a.count);
  }, [engagement]);

  // Timeline heatmap — last 30 days
  const heatmap = useMemo(() => {
    const days = 30;
    const buckets = Array.from({ length: days }, (_, i) => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      d.setDate(d.getDate() - (days - 1 - i));
      return { date: d, count: 0 };
    });
    logs.forEach(l => {
      if (!l.created_date) return;
      const d = new Date(l.created_date);
      d.setHours(0, 0, 0, 0);
      const idx = Math.floor((buckets[buckets.length - 1].date - d) / 86400000);
      const bucketIdx = days - 1 - idx;
      if (bucketIdx >= 0 && bucketIdx < days) buckets[bucketIdx].count += 1;
    });
    const max = Math.max(1, ...buckets.map(b => b.count));
    return { buckets, max };
  }, [logs]);

  if (loading) return (
    <div className="p-8 text-center text-gray-500 flex items-center justify-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading engagement…
    </div>
  );
  if (error)   return <div className="p-4 text-red-600">{error}</div>;
  if (!engagement || !enquiry) return <div className="p-4">Nothing to show.</div>;

  const grade = engagement.grade || 'Cold';
  const G = GRADE_META[grade] || GRADE_META.Cold;
  const scorePct = Math.min(100, Math.round((engagement.score || 0)));
  const lastContact = engagement.last_contact_at;
  const lastContactDays = daysAgo(lastContact);

  return (
    <div className="p-4 max-w-[1200px] mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <button onClick={() => navigate(`/engage/enquiries/${id}`)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
          <ChevronLeft className="w-4 h-4" /> Back to Enquiry
        </button>
      </div>

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className={cn('w-10 h-10 rounded-xl bg-gradient-to-br flex items-center justify-center text-white shadow-sm', G.tone)}>
          <G.icon className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white inline-flex items-center gap-2">
            {enquiry.name}
            <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', getEngageStatusBadge(enquiry.status))}>
              {ENGAGE_STATUS_LABEL[enquiry.status] || enquiry.status}
            </span>
          </h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Patient engagement dashboard · {enquiry.mobile || 'no mobile'}
          </p>
        </div>
      </div>

      {/* Score + Grade + Basics row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {/* Score gauge */}
        <div className={cn('rounded-xl p-4 border shadow-sm relative overflow-hidden', G.bg, 'border-gray-100 dark:border-gray-700')}>
          <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Engagement score</div>
          <div className="mt-2 flex items-end gap-3">
            <div className={cn('text-5xl font-bold tabular-nums', G.text)}>{engagement.score || 0}</div>
            <span className={cn('inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold', G.text, G.bg)}>
              <G.icon className="w-3.5 h-3.5" /> {G.label}
            </span>
          </div>
          <div className="mt-4 h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
            <div className={cn('h-full bg-gradient-to-r rounded-full transition-all', G.tone)} style={{ width: `${scorePct}%` }} />
          </div>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2">
            Composed from recency + frequency of activity across all touchpoints.
          </p>
        </div>

        {/* Touchpoints */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Total touchpoints</div>
          <div className="text-4xl font-bold text-gray-900 dark:text-white mt-1 tabular-nums">{engagement.total_touchpoints || 0}</div>
          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-2">All activity logs across notes, calls, WhatsApp, email, status changes.</p>
        </div>

        {/* Last contact */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Last contact</div>
          {lastContact ? (
            <>
              <div className={cn('text-lg font-bold mt-1', lastContactDays >= 7 ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white')}>
                {lastContactDays === 0 ? 'Today'
                 : lastContactDays === 1 ? 'Yesterday'
                 : `${lastContactDays} days ago`}
              </div>
              <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{fmt(lastContact)}</p>
            </>
          ) : (
            <div className="text-lg font-bold text-red-600 dark:text-red-400 mt-1">No contact yet</div>
          )}
        </div>
      </div>

      {/* Alerts */}
      {engagement.alerts?.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-1.5">
            <AlertCircle className="w-4 h-4 text-red-500" /> Alerts
          </h2>
          <div className="space-y-1.5">
            {engagement.alerts.map((a, i) => {
              const S = SEVERITY_META[a.severity] || SEVERITY_META.low;
              return (
                <div key={i} className={cn('flex items-start gap-2 rounded-lg p-2.5 border', S.tone)}>
                  <span className={cn('w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0', S.dot)} />
                  <div className="text-[13px]">{a.message}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Activity breakdown + heatmap */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {/* Breakdown */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-1.5">
            <Zap className="w-4 h-4 text-purple-500" /> Activity breakdown
          </h2>
          {breakdownRows.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-6 dark:text-gray-400">No activity logged yet.</p>
          ) : (
            <div className="space-y-2">
              {breakdownRows.map(r => {
                const M = LOG_TYPE_META[r.type] || LOG_TYPE_META.system;
                return (
                  <div key={r.type} className="flex items-center gap-2 text-sm">
                    <M.icon className={cn('w-3.5 h-3.5 flex-shrink-0', M.tone)} />
                    <span className="w-24 flex-shrink-0 text-[12px] text-gray-700 dark:text-gray-300">{M.label}</span>
                    <div className="flex-1 h-4 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-purple-400 to-indigo-500 rounded-full"
                           style={{ width: `${r.pct}%` }} />
                    </div>
                    <span className="text-[11px] font-medium tabular-nums text-gray-700 dark:text-gray-300 w-14 text-right">
                      {r.count} · {r.pct}%
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* 30-day heatmap */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4 text-teal-500" /> Activity — last 30 days
          </h2>
          <div className="grid gap-0.5" style={{ gridTemplateColumns: 'repeat(15, minmax(0, 1fr))' }}>
            {heatmap.buckets.map((b, i) => {
              const intensity = b.count === 0 ? 0 : Math.min(4, Math.ceil(b.count / heatmap.max * 4));
              const bgClass = [
                'bg-gray-100 dark:bg-gray-800/60',
                'bg-teal-100 dark:bg-teal-900/40',
                'bg-teal-300 dark:bg-teal-700',
                'bg-teal-400 dark:bg-teal-600',
                'bg-teal-500 dark:bg-teal-500',
              ][intensity];
              return (
                <div key={i}
                  className={cn('aspect-square rounded-sm', bgClass)}
                  title={`${b.date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}: ${b.count} touchpoint${b.count !== 1 ? 's' : ''}`}
                />
              );
            })}
          </div>
          <div className="mt-2 flex items-center justify-between text-[10px] text-gray-500 dark:text-gray-400">
            <span>{heatmap.buckets[0].date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</span>
            <div className="flex items-center gap-1">
              <span>Less</span>
              <span className="w-2.5 h-2.5 rounded-sm bg-gray-100 dark:bg-gray-800/60" />
              <span className="w-2.5 h-2.5 rounded-sm bg-teal-100 dark:bg-teal-900/40" />
              <span className="w-2.5 h-2.5 rounded-sm bg-teal-300 dark:bg-teal-700" />
              <span className="w-2.5 h-2.5 rounded-sm bg-teal-400 dark:bg-teal-600" />
              <span className="w-2.5 h-2.5 rounded-sm bg-teal-500 dark:bg-teal-500" />
              <span>More</span>
            </div>
            <span>Today</span>
          </div>
        </div>
      </div>

      {/* Recent timeline */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden shadow-sm">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
            <Clock className="w-4 h-4 text-blue-500" /> Recent activity ({Math.min(15, logs.length)} of {logs.length})
          </h2>
          <Link to={`/engage/enquiries/${id}`} className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400">
            Full timeline →
          </Link>
        </div>
        <div className="divide-y divide-gray-100 dark:divide-gray-700">
          {logs.slice(0, 15).map(log => {
            const M = LOG_TYPE_META[log.log_type] || LOG_TYPE_META.system;
            return (
              <div key={log.id} className="px-4 py-2.5 flex items-start gap-3">
                <div className="w-7 h-7 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center flex-shrink-0">
                  <M.icon className={cn('w-3.5 h-3.5', M.tone)} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">
                      {M.label}
                    </span>
                    <span className="text-[10px] text-gray-500 dark:text-gray-400">{fmt(log.created_date)}</span>
                  </div>
                  {log.message && (
                    <p className="text-[13px] text-gray-700 dark:text-gray-300 mt-0.5 line-clamp-2">{log.message}</p>
                  )}
                </div>
              </div>
            );
          })}
          {logs.length === 0 && (
            <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">No activity logs yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
